import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { Info } from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { directionalEdge, edgeFromTotals, type DirectionalEdge } from '@/lib/directionalEdge';
import { AssetDonut, EmptyChart, LongShortDonut, LeverageGauge } from '@/components/charts/PortfolioCharts';
import { intlLocale, fmtFixed, fmtUsd, ltr } from '@/lib/format';
import { AssetSplitSwitch } from '@/components/AssetType';
import { type AssetClass, instrumentOf, useInstruments } from '@/lib/instruments';

/****************
 * LONG VS SHORT · ASSET SPLIT · LEVERAGE -- ONE ROW, EVERY PAGE.
 *
 * Guillaume, 2026-09-25: these three are on the Dashboard, the trader page's
 * header, the trader profile and the session page, "but they are not
 * identical, they are not always in the same order, they are not fully and
 * properly centered, some have a small text in the Long Short that the others
 * don't. I want you to make the order, the info and the alignment to be
 * identical."
 *
 * They were two implementations: the Dashboard's own three cards (centred
 * titles, $ / # toggles, leverage weighted by size) and this row (left
 * titles, "$ / # Trades", Top Traded Assets first, a plain average leverage,
 * and the Bull/Bear verdict nobody else had). Now there is one: the
 * Dashboard's layout, in that order, with the verdict everywhere there is a
 * closed history to judge it on. The Dashboard renders this component too.
 ****************/

/** Everything the row draws, from whichever source a page has. */
export interface TradeMix {
  longNotional: number;
  shortNotional: number;
  longCount: number;
  shortCount: number;
  /** Biggest first. */
  byAsset: { symbol: string; notional: number; count: number }[];
  /** Total size over total margin: what leverage the MONEY runs at. Null with nothing sized. */
  weightedLeverage: number | null;
  tradeCount: number;
}

export const EMPTY_MIX: TradeMix = {
  longNotional: 0, shortNotional: 0, longCount: 0, shortCount: 0,
  byAsset: [], weightedLeverage: null, tradeCount: 0,
};

/** From a list of trade-like rows: the trader page, the profile, the session. */
export function tradeMixFromTrades(rows: any[]): TradeMix {
  let longNotional = 0, shortNotional = 0, longCount = 0, shortCount = 0;
  let levWeighted = 0, levNotional = 0;
  const assets: Record<string, { notional: number; count: number }> = {};
  for (const r of rows ?? []) {
    // `||` (not `??`) so an explicit 0 -- closed trades send current_size=0 --
    // falls through to the next field. 2026-05-25.
    const size = r.max_amount || r.max_size || r.initial_size || r.current_size || 0;
    const price = r.avg_entry_price || r.entry_price || r.mark_price || 0;
    const notional = r.nominal_value || (size * price) || 0;
    const side = String(r.direction ?? r.side ?? '').toLowerCase();
    if (side.includes('long')) { longNotional += notional; longCount++; }
    else { shortNotional += notional; shortCount++; }
    /*
     * Total size over total margin, the book's own leverage. 2026-10-05: this
     * averaged leverage weighted by size, and size is margin x leverage, so
     * leverage counted twice. One $1.7M silver long at 200x took commodities
     * to 171.7x across 74 positions (Guillaume: "sounds unreasonable"); size
     * over margin says 57.8x, and 12.7x without that one position.
     */
    const lev = Number(r.leverage) || 0;
    if (lev > 0 && notional > 0) { levWeighted += notional; levNotional += notional / lev; }
    const sym = r.symbol ?? r.asset ?? 'UNKNOWN';
    const a = (assets[sym] ??= { notional: 0, count: 0 });
    a.notional += notional;
    a.count++;
  }
  return {
    longNotional, shortNotional, longCount, shortCount,
    byAsset: Object.entries(assets)
      .map(([symbol, a]) => ({ symbol, ...a }))
      .sort((x, y) => y.notional - x.notional),
    weightedLeverage: levNotional > 0 ? levWeighted / levNotional : null,
    tradeCount: longCount + shortCount,
  };
}

const usd0 = (v: number) => fmtUsd(v, 0);
export const compactUsd = (v: number) => {
  const a = Math.abs(v);
  if (a >= 1_000_000) return ltr(`${v < 0 ? '-' : ''}$${fmtFixed(a / 1_000_000, 2)}M`);
  if (a >= 1_000) return ltr(`${v < 0 ? '-' : ''}$${fmtFixed(a / 1_000, 1)}k`);
  return usd0(v);
};
const plain = (v: number) => v.toLocaleString(intlLocale());

type Basis = 'open' | 'closed' | 'all';

/** The line under each title, so nobody guesses whether it is open or closed.
 *  Returns a translation key (traders namespace). */
function basisLabel(basis: Basis, isVol: boolean) {
  if (basis === 'open') return isVol ? 'charts.basis.openVol' : 'charts.basis.openCount';
  if (basis === 'all') return isVol ? 'charts.basis.allVol' : 'charts.basis.allCount';
  return isVol ? 'charts.basis.closedVol' : 'charts.basis.closedCount';
}

/** Title and control on one row, the subtitle under both: the old spacer
 *  left of a centred title was an empty block, and French titles wrapped
 *  beside it (2026-10-09). */
function ChartHeader({ title, subtitle, info, right }: {
  title: string; subtitle: string; info?: string; right?: ReactNode;
}) {
  return (
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1 min-w-0">
            <CardTitle className="text-sm truncate">{title}</CardTitle>
            {info && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-muted-foreground cursor-help"><Info size={12} /></span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px] text-xs">{info}</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      {subtitle ? <CardDescription className="text-xs">{subtitle}</CardDescription> : null}
    </CardHeader>
  );
}

function MetricToggle({ value, onChange }: { value: 'volume' | 'count'; onChange: (v: 'volume' | 'count') => void }) {
  return (
    <Tabs value={value} onValueChange={(v) => onChange(v as 'volume' | 'count')}>
      <TabsList className="h-6">
        <TabsTrigger value="volume" className="text-[10px] px-1.5 h-4">$</TabsTrigger>
        <TabsTrigger value="count" className="text-[10px] px-1.5 h-4">#</TabsTrigger>
      </TabsList>
    </Tabs>
  );
}

/** Centred in the card, whatever the chart's own width. */
function Centred({ children }: { children: ReactNode }) {
  return <div className="flex justify-center"><div className="w-fit max-w-full">{children}</div></div>;
}

const pct = (a: number, b: number) => (a + b > 0 ? Math.round((a / (a + b)) * 100) : 0);

export function ChartsRow({
  mix,
  basis = 'closed',
  scopeNote = '',
  edge,
  edgeNote,
  footer,
  assetClass = 'all',
  onAssetClassChange,
  hideBasis = false,
}: {
  /** The page's Open/Closed toggle already says it: no basis line under the titles (round 8 #206). */
  hideBasis?: boolean;
  mix: TradeMix;
  basis?: Basis;
  /** Who the row is about, before the basis line ("This session · "). */
  scopeNote?: string;
  /** Realised PnL by direction. Absent where there is no closed history. */
  edge?: DirectionalEdge | null;
  /** What the verdict was judged on, when it is less than everything. */
  edgeNote?: string | null;
  /** A line under all three, the same on every card (the Dashboard's open-count note). */
  footer?: ReactNode;
  /** The page's asset-type choice (beside Live/Paper or Live/All), 2026-10-04. */
  assetClass?: AssetClass | 'all';
  /** Sets the page's type switch. Pages without one drill inside the card instead. */
  onAssetClassChange?: (c: AssetClass | 'all') => void;
}) {
  const { t } = useTranslation('traders');
  const { t: tc } = useTranslation('common');
  // By trade count first, as the Long / Short card reads it (2026-10-20).
  const [lsMetric, setLsMetric] = useState<'volume' | 'count'>('count');
  const [assetMetric, setAssetMetric] = useState<'volume' | 'count'>('volume');
  /*
   * The asset split follows the page's asset-type choice (Guillaume,
   * 2026-10-04: beside Live/Paper on the Dashboard, beside Live/All on the
   * Traders page, not inside this box). An aggregated "Other" slice has no
   * single type, so it shows under All only.
   *
   * One donut, two levels (Guillaume, 2026-10-06, §8b.2 of the Beyond Crypto
   * study): under All it splits by asset TYPE and a click on a type selects
   * it; under a type it splits by asset, as before. A page with its own
   * switch drives it; the trader and session pages drill inside the card.
   */
  const [localClass, setLocalClass] = useState<AssetClass | 'all'>('all');
  const assetClassView = onAssetClassChange ? assetClass : (assetClass !== 'all' ? assetClass : localClass);
  const pickClass = onAssetClassChange ?? setLocalClass;
  const { data: instrumentReg } = useInstruments();

  const lsVol = lsMetric === 'volume';
  const longVal = lsVol ? mix.longNotional : mix.longCount;
  const shortVal = lsVol ? mix.shortNotional : mix.shortCount;
  const otherLong = lsVol ? mix.longCount : mix.longNotional;
  const otherShort = lsVol ? mix.shortCount : mix.shortNotional;

  const assetVol = assetMetric === 'volume';
  /* Every asset comes on its own and has a type: there is no "Other"
     (Guillaume, 2026-10-09: "The Category Other should not exist"). */
  const classOf = (a: { symbol: string }): AssetClass => instrumentOf(a.symbol, instrumentReg).class;
  /* Forex is not shown until a broker can execute it (Guillaume, 2026-10-09):
     its rows leave the split and its total, not the data. */
  const shownAssets = mix.byAsset.filter((a) => classOf(a) !== 'forex');
  const assetRows = shownAssets
    .filter((a) => assetClassView === 'all' || classOf(a) === assetClassView)
    .map((a) => ({
      symbol: instrumentOf(a.symbol, instrumentReg).display,
      value: assetVol ? a.notional : a.count,
    }))
    .sort((x, y) => y.value - x.value);

  /* Under All, the same trades by type. Indexes are filed with stocks
     (Guillaume, 2026-10-05: "market indicators stay with stocks"). A book of a
     single type keeps the asset view: a ring that says "100% crypto" tells
     nobody anything. */
  const typeOf = (a: { symbol: string }): AssetClass => {
    const c = classOf(a);
    return c === 'indicator' ? 'stock' : c;
  };
  // Every trade in one of the types (Guillaume, 2026-10-09: "everything
  // should be in one of the 3 category").
  const byType = new Map<AssetClass, number>();
  if (assetClassView === 'all') {
    for (const a of shownAssets) {
      const v = assetVol ? a.notional : a.count;
      byType.set(typeOf(a), (byType.get(typeOf(a)) ?? 0) + v);
    }
  }
  const typeView = assetClassView === 'all' && [...byType.values()].filter((v) => v > 0).length > 1;
  const typeRows = [...byType]
    .filter(([, v]) => v > 0)
    .map(([c, v]) => ({ symbol: c, kind: c, label: tc(`asset.split.${c}`), value: v }))
    .sort((x, y) => y.value - x.value);
  const donutRows = typeView ? typeRows : assetRows;

  const n = mix.tradeCount;
  const unit = basis === 'open' ? 'open' : basis === 'all' ? 'all' : 'closed';

  return (
    <div data-tour="tr-charts" className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      <Card className="glass-card h-full flex flex-col">
        <ChartHeader
          title={t('charts.longVsShort')}
          subtitle={hideBasis ? '' : scopeNote + t(basisLabel(basis, lsVol))}
          right={<MetricToggle value={lsMetric} onChange={setLsMetric} />}
        />
        <CardContent className="pb-4 flex-1 flex flex-col justify-center">
          {longVal + shortVal === 0 ? <EmptyChart /> : (
            <Centred>
              <LongShortDonut
                longValue={longVal}
                shortValue={shortVal}
                format={lsVol ? compactUsd : plain}
                edge={edge ?? undefined}
                edgeSample={edge ? edgeNote ?? null : null}
                footnote={
                  <>
                    {lsVol ? t('charts.byCount') : t('charts.bySize')}:{' '}
                    <bdi dir="ltr">
                    <span className="text-success">{t('charts.pctLong', { pct: pct(otherLong, otherShort) })}</span>
                    {' / '}
                    <span className="text-danger">{t('charts.pctShort', { pct: pct(otherShort, otherLong) })}</span>
                    </bdi>
                  </>
                }
              />
            </Centred>
          )}
          {footer}
        </CardContent>
      </Card>

      <Card className="glass-card h-full flex flex-col">
        <ChartHeader
          title={t('charts.assetSplit')}
          subtitle={hideBasis ? '' : scopeNote + t(basisLabel(basis, assetVol))}
          right={<MetricToggle value={assetMetric} onChange={setAssetMetric} />}
        />
        <CardContent className="pb-4 flex-1 flex flex-col justify-center">
          {donutRows.reduce((s, r) => s + r.value, 0) === 0 ? <EmptyChart /> : (
            <Centred>
              <AssetDonut
                rows={donutRows}
                format={assetVol ? compactUsd : plain}
                onSliceClick={typeView ? (s) => s.kind && pickClass(s.kind) : undefined}
              />
            </Centred>
          )}
          {typeView && (
            <p className="text-[10px] text-muted-foreground text-center mt-2">{t('charts.byType.hint')}</p>
          )}
          {!onAssetClassChange && assetClass === 'all' && localClass !== 'all' && (
            <button
              type="button"
              className="text-[10px] text-muted-foreground underline hover:text-foreground mx-auto mt-2 block"
              onClick={() => setLocalClass('all')}
            >
              {t('charts.byType.back')}
            </button>
          )}
          {footer}
        </CardContent>
      </Card>

      <Card className="glass-card h-full flex flex-col">
        <ChartHeader
          title={t('charts.leverage')}
          subtitle={hideBasis ? '' : scopeNote + (basis === 'open' ? t('charts.leverage.open')
            : basis === 'all' ? t('charts.leverage.all') : t('charts.leverage.closed'))}
          info={t('charts.leverage.info')}
        />
        <CardContent className="pb-4 flex-1 flex flex-col justify-center">
          {mix.weightedLeverage === null ? <EmptyChart /> : (
            <div className="flex flex-col items-center justify-center">
              <LeverageGauge leverage={mix.weightedLeverage} />
              <p className="text-[10px] text-muted-foreground mt-1">
                {t(`charts.across.${unit}`, { count: n, n: n.toLocaleString(intlLocale()) })}
              </p>
            </div>
          )}
          {footer}
        </CardContent>
      </Card>
    </div>
  );
}

/** The verdict from a page's closed trades, stated as the sample it is.
 *  `t` is optional: without it the note is in the language currently loaded. */
export function edgeOf(
  closedTrades: any[] | undefined, closedTotal?: number, scopeNote?: string,
  t?: (key: string, opts?: Record<string, unknown>) => string,
) {
  const rows = closedTrades ?? [];
  if (!rows.length) return { edge: null as DirectionalEdge | null, note: null as string | null };
  const tr = t ?? ((k: string, o?: Record<string, unknown>) => String(i18n.t(k, o)));
  const note = scopeNote
    ?? (closedTotal && closedTotal > rows.length
      ? tr('traders:charts.judgedLoaded', { n: rows.length.toLocaleString(intlLocale()), total: closedTotal.toLocaleString(intlLocale()) })
      : tr('traders:charts.judgedAll', { n: rows.length.toLocaleString(intlLocale()) }));
  return { edge: directionalEdge(rows), note };
}

export { edgeFromTotals };
